import type { ReactNode } from 'react';
import { X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { StepIndicator } from '@/components/ui/step-indicator';
import { useTranslation } from '@/hooks/useTranslation';
import { cn } from '@/lib/utils';
import {
  connectFlowSteps,
  type ConnectFlowJourney,
  type ConnectFlowStep,
} from './connectHelpers';

/**
 * The one header band of the bank-connection journey: `[logo + title]` on the
 * left, the step strip in the middle, an optional close on the right.
 *
 * WHY IT EXISTS. Four surfaces sit on this journey — Dialog A (choose your
 * bank), the bank's own page, `/bank-callback`, and Dialog B or C — and the bank
 * page in the middle is not ours, so the journey can never be one continuous
 * wizard. Continuity has to come from the CHROME instead: leave a band, come
 * back to the same band. Without this component all four would hand-roll
 * `[logo + title] [strip] [close]`, which is precisely the duplication that
 * extracting `StepIndicator` just removed from the three import wizards.
 *
 * ── IT PAINTS THE BAND ITSELF, AND WHY ──────────────────────────────────────
 * It carries the same classes as `FullscreenDialogHeader`
 * (`components/ui/fullscreen-dialog.tsx:62`) rather than being wrapped in one,
 * because `BankCallback` is a BARE PAGE with no Radix Dialog anywhere — a
 * component that only worked inside `FullscreenDialogContent` would be useless
 * on exactly the surface this was built to reach.
 *
 * So inside a dialog it REPLACES `FullscreenDialogHeader`; it is the first child
 * of `FullscreenDialogContent`, never nested inside a second band.
 *
 * ── THE TITLE IS A SLOT ─────────────────────────────────────────────────────
 * Same reason. `FullscreenDialogTitle` is `Radix Dialog.Title` and throws
 * without a Dialog context, so a `title: string` prop could not serve the
 * callback. Dialogs pass `<FullscreenDialogTitle>…</FullscreenDialogTitle>`
 * (which is also what satisfies Radix's accessible-name requirement); the
 * callback passes a plain `<h1>`.
 *
 * ── THE LOGO IS WHITE, DELIBERATELY ─────────────────────────────────────────
 * `/releve_name_white.svg`, exactly as `CAMTImportDialog` uses it. NOT the
 * light/dark pair the callback page uses below this band: that pair is keyed on
 * `.dark`, and this band is dark on the two most-used presets in LIGHT mode —
 * so the pair would render a black logo on a teal-dark band there. (White is
 * weak on the `calm` preset, the known pre-existing
 * `fullscreen-form-dialog.tsx:199` issue, not something this introduces.)
 */
export interface ConnectFlowHeaderProps {
  /**
   * The heading element itself, not its text — see the header comment. Dialogs
   * pass `FullscreenDialogTitle`, the callback page a plain heading.
   */
  title: ReactNode;
  /**
   * The journey whose strip to show. ABSENT OR NULL ⇒ NO STRIP, which is the
   * honest default for a dialog opened outside the flow (Dialog B's direct door
   * from Edit Account) and for a callback whose state carries no stamp.
   */
  journey?: ConnectFlowJourney | null;
  /**
   * An explicit step list, for a connect journey that is NOT a bank journey
   * (the Stripe sales channel: connect → authorize → finish). Takes precedence
   * over `journey`; absent or null falls back to it. Same `{ key, labelKey }`
   * shape `connectFlowSteps` returns, so the strip renders identically.
   */
  steps?: ConnectFlowStep[] | null;
  /**
   * Where on the strip this surface stands: a step KEY (`'setUp'`, `'finish'`,
   * …), `'last'` for the closing screen of a journey whose length depends on the
   * journey, or a zero-based index. Ignored without a `journey` or `steps`.
   *
   * A KEY is the honest form for any surface that can occupy more than one
   * position — Dialog C is `setUp` while the user decides and `finish` while the
   * batch runs — because it survives a journey growing a step. An unknown key
   * falls back to the last step rather than dropping the highlight.
   */
  currentStep?: number | 'last' | ConnectFlowStep['key'];
  /** Omitted on the callback page, which has nothing to close. */
  onClose?: () => void;
  /**
   * Greys out the close button. For a dialog in the middle of work it cannot
   * abandon — the ✕ is an escape hatch like any other, and a conditionally
   * mounted dialog closed mid-batch takes its failure report with it.
   */
  closeDisabled?: boolean;
  className?: string;
}

/** Index of `currentStep` on `steps` — see the `currentStep` prop. */
function resolveStepIndex(
  steps: ConnectFlowStep[],
  currentStep: number | 'last' | string,
): number {
  if (typeof currentStep === 'number') return currentStep;
  if (currentStep !== 'last') {
    const index = steps.findIndex((step) => step.key === currentStep);
    if (index >= 0) return index;
  }
  return steps.length - 1;
}

export function ConnectFlowHeader({
  title,
  journey,
  steps: explicitSteps,
  currentStep = 0,
  onClose,
  closeDisabled = false,
  className,
}: Readonly<ConnectFlowHeaderProps>) {
  const { t } = useTranslation();

  const steps = explicitSteps ?? (journey ? connectFlowSteps(journey) : null);

  return (
    <div
      className={cn(
        'flex items-center justify-between border-b border-dialog-header-border bg-dialog-header text-dialog-header-foreground px-4 md:px-6 py-2',
        className,
      )}
    >
      <div className="flex items-center gap-3">
        <img src="/releve_name_white.svg" alt="Releve" className="h-6 w-6 object-contain" />
        {title}
      </div>

      {steps && (
        <StepIndicator
          steps={steps.map((step) => ({ key: step.key, label: t(step.labelKey) }))}
          currentIndex={resolveStepIndex(steps, currentStep)}
          ariaLabel={t('common.stepProgress')}
        />
      )}

      {onClose ? (
        <Button
          variant="ghost"
          size="icon"
          onClick={onClose}
          disabled={closeDisabled}
          aria-label={t('common.close')}
          className="h-8 w-8 text-dialog-header-foreground hover:bg-dialog-header-foreground/20 hover:text-dialog-header-foreground"
        >
          <X className="h-4 w-4" />
        </Button>
      ) : (
        // Keeps the strip in the middle of the band rather than shoved against
        // the right edge on the one surface with no close button.
        <div className="h-8 w-8" aria-hidden="true" />
      )}
    </div>
  );
}
